import { defineStore } from "pinia";
import Threads from "@/api/threads";
import { useToast } from "vue-toastification";
import { formatInteraction } from "@/api/interactions";
import { addInteractionTagByOrganization } from "@/api/interaction_tags";

export const useThreadsContextStore = defineStore("threadsContext", {
  state: () => ({
    threads: {}, // source of truth
    sortedThreads: [],
    isLoadingById: {},
    totalCount: 0,
    isLoading: false,
    isSuccess: false,
  }),
  getters: {
    getThreadById: (state) => (id) => {
      return state.threads[id] || null;
    },
    formattedInteractionsByThread: (state) => {
      const normalize = (v) =>
        v == null ? Number.POSITIVE_INFINITY : Number(v);

      const result = {};

      for (const [threadId, thread] of Object.entries(state.threads)) {
        const interactions = thread?.interactions ?? [];

        result[threadId] = interactions
          .map((i) => formatInteraction({ ...i, isReadOnly: true }))
          .sort((a, b) => normalize(a.short_id) - normalize(b.short_id));
      }

      return result;
    },
    getFormattedInteractionsByThread: (state) =>
      (threadId) =>
        state.formattedInteractionsByThread?.[threadId] ?? [],
  },
  actions: {
    async getThreadIdForId(unknownId) {
      this._setThreadLoading(unknownId, true);
      try {
        const data = await Threads.getThreadForId(unknownId);
        return data.thread_id;
      }
      catch (err) {
        console.error(
          "Error checking if ID is Thread ID",
          unknownId,
          err,
        );
        return unknownId;
      }
      finally {
        this._setThreadLoading(unknownId, false);
      }
    },
    async fetchThread(id) {
      this._setThreadLoading(id, true)
      try {
        const data = await Threads.getThread(id);
        this._addPlaceholderStatus(data)
        this.threads[id] = data;
      } catch (err) {
        console.error(
          `Error fetching thread for thread id ${id}. The error is: ${err}`
        );
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    async fetchThreads(filters = {}) {
      this.isLoading = true
      try {
        const { data, total_count, limit, page } = await Threads.getThreads(filters)

        data.forEach(thread => this._addPlaceholderStatus(thread))

        // create a map of threads for fast lookup, will be the source of truth too
        const dataObj = data.reduce((threads, thread) => {
          threads[thread._id] = thread;
          return threads;
        }, {});

        this.totalCount = total_count
        this.threads = dataObj
        this.sortedThreads = data
        this.isSuccess = true
      } catch(err) {
        console.error(`Error in fetchTreads method. The error is: ${err}`)
        this.isSuccess = false
      }
       finally {
        this.isLoading = false
      }
    },
    async patchThread(id, payload) {
      this._setThreadLoading(id, true)
      try {
        const data = await Threads.patchThread(id, payload)
        this.threads[id] = data
        this._patchThreadElInArray(id, data)
        return data
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    async patchSeenBy(id, payload) {
      this._setThreadLoading(id, true)
      try {
        const data = await Threads.patchSeenBy(id, payload)
        console.log("data: ", data)
  
        // update the thread dict and thread array
        this.threads[id] = data
        this._patchThreadElInArray(id, data)
        return data
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    async patchTags(id, tag, organizationId = null) {
      this._setThreadLoading(id, true)
      try {
        let thread = null
        let tags = null
        if(organizationId) {
          const { thread: threadObj, interaction_tag: tagObj } = await addInteractionTagByOrganization(organizationId, tag, id)
          thread = threadObj
          tags = tagObj?.tags || []
        } else {
          thread = await Threads.patchTags(id, tag)
        }
        this.threads[id] = thread
        this._patchThreadElInArray(id, thread)
        return { thread, tags }
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    async patchTicketPriority(id, ticketPriorityId) {
      await Threads.patchTicketPriority(id, ticketPriorityId)
      if (this.threads[id]) this.threads[id].ticket_priority_id = ticketPriorityId
      this._patchThreadElInArray(id, { ticket_priority_id: ticketPriorityId })
    },
    async patchUnreadBy(id, payload) {
      this._setThreadLoading(id, true)
      try {
        const data = await Threads.patchUnreadBy(id, payload)
        console.log("data: ", data)

        // update the thread dict and thread array
        this.threads[id] = data
        this._patchThreadElInArray(id, data)
        return data
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    async patchToTicket(id) {
      this._setThreadLoading(id, true)
      try {
        const data = await Threads.patchToTicket(id)
        console.log("data: ", data)

        // update states
        this.threads[id] = data
        this._patchThreadElInArray(id, data)
        return data
      } finally {
        this._setThreadLoading(id, false)
      }
    },
    _patchThreadElInArray(id, payload = {}) {
      const index = this.sortedThreads.findIndex(thread => thread._id === id)
      // console.log("index: ", index)
      if(index === -1) return
      Object.assign(this.sortedThreads[index], payload);
    },
    _setThreadLoading(id, value) {
      this.isLoadingById[id] = value
    },
    removeThreadFromSortedArray(id) {
      const index = this.sortedThreads.findIndex(thread => thread._id === id)
      if(index === -1) return
      this.sortedThreads.splice(index, 1)
    },
    // TODO: REMOVE THIS WHEN BE IS DONE
    // filling the status PROP as a placeholder so I can work on FE before BE is completed
    _addPlaceholderStatus(threadObj, status = "Ongoing") {
      if(threadObj.status) return
      threadObj.status = status
    }
  },
});
